/**
 * Shared course content and constants.
 *
 * Kept in one place so the live page, the self-paced page and the challenge
 * pages cannot drift apart in wording, price framing or guarantee terms.
 */

/**
 * Preview video.
 *
 * Chosen by scanning the Lumiwealth YouTube channel on 2026-08-04 for the best
 * combination of length and audience: "From $0 to Millions: My Trading Strategy
 * & AI Bootcamp!" is 4:09 and, at ~500 views, the strongest performer on the
 * channel. It is also on-message: Rob tells the 2008 story himself, so it does
 * double duty as proof and as the Star/Story/Solution beat.
 *
 * Do NOT go back to the old 1h40m "A Bootcamp Preview" recording. Nobody
 * watches a 100-minute video before buying.
 */
export const PREVIEW_VIDEO_ID = 'iZKrd2TdGVQ';
export const PREVIEW_VIDEO_LENGTH = '4 minutes';
export const PREVIEW_VIDEO_URL = `https://www.youtube.com/watch?v=${PREVIEW_VIDEO_ID}`;

/** Guarantee window in days. Keep every course surface aligned to this value. */
export const GUARANTEE_DAYS = 60;

export const GUARANTEE_HEADLINE = `${GUARANTEE_DAYS}-day money-back guarantee`;
export const GUARANTEE_BODY =
  `Enroll, attend, and if it is not for you, ask for a refund within ${GUARANTEE_DAYS} days ` +
  'and you get your money back. No forms, no argument, and no questions asked.';

export const NO_CODING_BADGE = 'No coding experience required';

/** What a buyer has after the very first session. Attacks Hormozi's time-delay term. */
export const FIRST_NIGHT_PROMISE =
  'You will have a working AI trading workspace and a reviewed paper trade on night one.';

/**
 * Rob's story.
 *
 * Brunson's Star/Story/Solution. The vulnerability opener only works if it is
 * immediately followed by proof of competence. An earlier version stopped at
 * "I blew up my account in 2008" and never recovered, which read as "let me
 * teach you what I got wrong". Rob's note, 2026-08-04: if you are going to
 * show vulnerability you have to show the capability that came after it.
 *
 * Every credential here is from MarketingManager/docs/ROB-PROFILE-POSITIONING.md.
 * Do NOT invent or inflate. Specifically: he was an early Voyager employee and
 * Software Technical Lead, NOT a co-founder; Greystone is a commercial real
 * estate lender and the verifiable figure is $3B+ in transactions processed,
 * not AUM; Voyager's $1B+ is peak market cap, not assets.
 *
 * The family account is deliberately described without figures. Rob is not
 * certain of the exact numbers, and a specific multiple would be a performance
 * claim we cannot substantiate.
 */
export const ROB_STORY = {
  heading: 'Why you should listen to me',
  paragraphs: [
    'In 2008 I blew up my own trading account. I had conviction, I had opinions, and I had no way of knowing whether any of it was actually true before I put money behind it.',
    'So I stopped guessing. I taught myself to backtest everything, then went and got a Master of Finance at Schulich. When my father passed away I took over my family\u2019s accounts, and that same discipline grew them many times over. That money is still working today, and it is the reason I take this seriously rather than treating it as a hobby.',
    'After grad school I built a software company and sold it, which paid for a couple of years of travelling. Then I landed in New York at Greystone as VP of Fintech, where I built loan software that processed over $3 billion in transactions and got a close look at how serious money actually operates. After that I was one of the first engineers at Voyager Digital, building the trading and brokerage infrastructure from scratch and growing the team, at a company that reached a $1B+ peak market cap.',
    'Along the way I wrote Lumibot, now past 1,880 stars on GitHub, and built more than a thousand trading systems. What changed in the last year is that AI agents got good enough to do work that used to need a desk of analysts: read the filings, pull live data, run the analysis across every asset class, and place the trade. Almost nobody knows what these tools can actually do yet. That gap is the entire reason this bootcamp exists.',
  ],
};

/**
 * ============================================================================
 * WHAT THIS IS WORTH: outcome-based, not cost-substitution
 * ============================================================================
 *
 * The previous version of this section priced the bootcamp against five older
 * Lumiwealth courses. Rob killed it on 2026-08-04 and he was right: those
 * courses are dated, they are NOT included in the bootcamp, and calling them
 * "the same curriculum" was misleading. Pricing 15 hours of his time also
 * lands at roughly what the course costs, so it argues for nothing.
 *
 * What replaced it answers the question a buyer is actually asking: what does
 * this do for my life? Each pillar carries a real, published, linked figure
 * about THEM: their time, their skills, and their process. None of it is a
 * claim about investment returns, which we neither make nor can make.
 *
 * RULES:
 *   - Every number cites a real published source, linked, with a date.
 *   - Never a return, profit or performance claim, direct or implied.
 *   - Never price a line against a product the buyer does not receive.
 */

/** Date the external figures below were last verified. */
export const PRICES_VERIFIED_ON = '2026-08-04';

export const WORTH_HEADING = 'What is this actually worth to you?';
export const WORTH_LEDE =
  'No projections about what you will make, because nobody can honestly give you those. '
  + 'Here is what changes for you instead, and where each number comes from.';

export const WORTH_PILLARS = [
  {
    key: 'research-gap',
    img: '/images/courses/worth-research-gap.webp',
    stat: '6 minutes',
    statLabel: 'is what the median retail investor spends researching a stock before buying it',
    heading: 'You close the gap between the research you do and the research the decision deserves',
    body:
      "Six minutes is the median. Buffett's own answer for doing it properly is six to eight hours a "
      + 'week per portfolio. Almost nobody has that, so almost everybody trades on a fraction of the '
      + 'homework the decision deserves. An agent that reads the filings, pulls the live data and shows '
      + 'you its sources closes that gap without asking for your evenings.',
    source: 'Median research time: Forbes, on retail order-flow research (2025). Six to eight hours: Warren Buffett.',
    href: 'https://www.forbes.com/sites/boazsobrado/2025/05/30/6-minutes-of-retail-trader-research-drives-20-of-stock-market-volume/',
  },
  {
    key: 'time-back',
    img: '/images/courses/worth-time-back.webp',
    stat: '~1.5 hours',
    statLabel: 'a day of pre-market prep, collapsed to about ten minutes',
    heading: 'You get your mornings back',
    body:
      'That figure is not ours. It is what a trader reported in a post that 529 people upvoted: one to '
      + 'two hours of scanning tickers, news and sentiment every morning, down to roughly ten minutes '
      + 'once the AI does the pass first. Across a year of trading days that is hundreds of hours you '
      + 'are not spending in front of a screen.',
    source: 'r/Daytrading, "AI now saves me 2 hours every morning" (529 upvotes, 2025).',
    href: 'https://www.reddit.com/r/Daytrading/comments/1oeo3lg/',
  },
  {
    key: 'skill',
    img: '/images/courses/worth-skill.webp',
    stat: '+$18,000',
    statLabel: 'a year is the measured salary premium on job postings that ask for AI skills',
    heading: 'The skill is worth something whether or not you ever place a trade',
    body:
      'What you actually learn here is how to direct an AI agent to do real work and how to check it. '
      + 'the trading is the vehicle. Lightcast puts the premium on AI-skilled roles at 28%, about '
      + '$18,000 a year, across 1.3 billion job postings. PwC, across a billion job ads in 27 countries, '
      + 'puts it at 62%. That is a recurring number against a one-time price.',
    source: 'Lightcast AI Skills Report (2025); PwC Global AI Jobs Barometer (2026).',
    href: 'https://www.prnewswire.com/news-releases/new-lightcast-report-ai-skills-command-28-salary-premium-as-demand-shifts-beyond-tech-industry-302511141.html',
  },
  {
    key: 'process',
    img: '/images/courses/worth-process.webp',
    stat: '1 decision',
    statLabel: 'is all it takes for a verification habit to pay for the whole thing',
    heading: 'You stop finding out you were wrong from the price',
    body:
      'Rob blew up an account in 2008 holding a conviction he had no way to test. The entire method here '
      + 'is built around making the AI show its evidence so you can catch it before you act, not after. '
      + 'We are not going to put a dollar figure on a mistake you did not make. You already know what '
      + 'one of those costs you.',
    source: null,
    href: null,
  },
];


/**
 * The cohort trading competition.
 *
 * Paper-only by design. The competition reinforces the same controlled,
 * reviewable progression taught in the curriculum without asking students to
 * risk live capital for a class leaderboard.
 */
export const COMPETITION = {
  name: 'The cohort paper-trading competition',
  stackDetail: 'Place a reviewed paper trade every week, keep one fixed balance, and follow the leaderboard together.',
  heading: 'One paper trade every week',
  body:
    'Students can use a fixed paper-trading account for the length of the bootcamp. '
    + 'Each week adds a trade from that session\'s workflow, and the class reviews both the result and how it was produced.',
  points: [
    'Start from the same paper balance in week one, with no deposits or resets.',
    'Place or queue one reviewed trade tied to each week\'s lesson.',
    'Keep an auditable history throughout the cohort.',
    'Review the leaderboard, decisions, and controls together on the call.',
    'Opt out and stay off the board if you would rather keep your numbers private.',
  ],
};

export const SEO_TOPICS = [
  'Agentic trading',
  'Claude Code',
  'AI stock analysis',
  'AI options trading',
  'Codex',
  'Algorithmic trading',
];

/**
 * What you receive. A plain list of what is in the box, kept separate from the
 * worth argument above. Only the recording library carries a price, and that
 * price comes from the live catalog because it is a product anyone can buy on
 * its own. Nothing else gets an invented number.
 */
export const buildValueStack = (selfPacedDisplay) => {
  const items = [
    { name: 'Five live sessions with Rob', detail: 'One to two hours each, your questions answered on camera.' },
    { name: 'Your own setup reviewed by Rob', detail: 'Bring what you built and he goes through it live.' },
    { name: 'Every prompt, checklist and script used in class', detail: 'The exact working material, not summaries of it.' },
    { name: COMPETITION.name, detail: COMPETITION.stackDetail },
  ];

  if (selfPacedDisplay) {
    items.push({
      name: 'The complete recording library, yours to keep',
      detail: 'Every session, in your portal, forever.',
      value: selfPacedDisplay,
      valueNote: 'sold on its own as the self-paced course',
    });
  }

  return items;
};
